import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  AddCircleOutline as CreatePledgeIcon,
  Search as CheckPledgeIcon,
  LockOpen as ReleaseIcon,
  PersonOutline as PersonIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  ReceiptLong as TxIcon,
} from '@mui/icons-material';
import Navbar from './Navbar';
import CallRemindersPanel from './CallRemindersPanel';
import baseURL from '../../baseURL';
import { receiptService } from '../../services/receiptService';

const ACTION_SHORTCUTS = [
  {
    key: 'create',
    label: 'Create Pledge',
    hint: 'New loan ticket',
    path: '/pledge/create',
    icon: CreatePledgeIcon,
    color: '#0f766e',
    bg: 'rgba(15, 118, 110, 0.1)',
  },
  {
    key: 'check',
    label: 'Check Pledge',
    hint: 'Search / list pledges',
    path: '/pledge/list',
    icon: CheckPledgeIcon,
    color: '#1d4ed8',
    bg: 'rgba(29, 78, 216, 0.1)',
  },
  {
    key: 'release',
    label: 'Release',
    hint: 'Release pledge',
    path: '/release',
    icon: ReleaseIcon,
    color: '#b45309',
    bg: 'rgba(180, 83, 9, 0.1)',
  },
  {
    key: 'scan',
    label: 'Scan Fingerprint',
    hint: 'Find by biometric',
    path: null,
    icon: FingerprintIcon,
    color: '#6d28d9',
    bg: 'rgba(109, 40, 217, 0.1)',
    primary: true,
  },
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [fingerprintScanning, setFingerprintScanning] = useState(false);
  const [fingerprintMatches, setFingerprintMatches] = useState([]);
  const [showFingerprintResults, setShowFingerprintResults] = useState(false);
  const [fingerprintMessage, setFingerprintMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const token = localStorage.getItem('token');
        const profileResponse = await fetch(`${baseURL}/auth/profile`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        const profileData = await profileResponse.json();

        if (!profileResponse.ok) {
          throw new Error(profileData.message || 'Failed to fetch profile');
        }

        const transactionsResponse = await fetch(`${baseURL}/auth/transactions`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        const transactionsData = await transactionsResponse.json();

        if (!transactionsResponse.ok) {
          throw new Error(transactionsData.message || 'Failed to fetch transactions');
        }

        setUserData(profileData.data.user);
        setTransactions(transactionsData.data || []);
      } catch (err) {
        console.error('Dashboard data fetch failed', err);
        setLoadError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFingerprintScan = async () => {
    try {
      setError(null);
      setFingerprintScanning(true);
      setFingerprintMessage('Scanning fingerprint… Place finger on the scanner.');
      setFingerprintMatches([]);

      const result = await receiptService.searchPledgeByFingerprint(60);

      if (result.matches && result.matches.length > 0) {
        const activeMatches = result.matches.filter(
          (pledge) => pledge.status !== 'RELEASED' && pledge.loanDetails?.released !== true
        );

        if (activeMatches.length > 0) {
          const customerCodes = [...new Set(activeMatches.map((p) => p.customer?.code).filter(Boolean))];
          const token = localStorage.getItem('token');
          const allPledgesPromises = customerCodes.map(async (code) => {
            try {
              const response = await fetch(`${baseURL}/pledges/client/${code}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.pledges) {
                  return data.pledges.filter(
                    (p) => p.status !== 'RELEASED' && p.loanDetails?.released !== true
                  );
                }
              }
              return [];
            } catch (err) {
              console.error(`Error fetching pledges for customer ${code}:`, err);
              return [];
            }
          });

          const allPledges = (await Promise.all(allPledgesPromises)).flat();
          const fingerprintMatchedIds = new Set(activeMatches.map((p) => p._id));
          const fingerprintScores = {};
          activeMatches.forEach((p) => {
            fingerprintScores[p._id] = p.fingerprintMatchScore;
          });

          const pledgesWithMatchInfo = allPledges.map((pledge) => ({
            ...pledge,
            fingerprintMatched: fingerprintMatchedIds.has(pledge._id),
            fingerprintMatchScore: fingerprintScores[pledge._id] || 0,
          }));

          pledgesWithMatchInfo.sort((a, b) => {
            if (a.fingerprintMatched && !b.fingerprintMatched) return -1;
            if (!a.fingerprintMatched && b.fingerprintMatched) return 1;
            if (a.fingerprintMatched && b.fingerprintMatched) {
              return (b.fingerprintMatchScore || 0) - (a.fingerprintMatchScore || 0);
            }
            return new Date(b.date) - new Date(a.date);
          });

          setFingerprintMatches(pledgesWithMatchInfo);
          setShowFingerprintResults(true);

          const matchedCount = pledgesWithMatchInfo.filter((p) => p.fingerprintMatched).length;
          const otherCount = pledgesWithMatchInfo.length - matchedCount;
          setFingerprintMessage(
            `Found ${matchedCount} fingerprint match(es) and ${otherCount} other active pledge(s). Quality: ${result.scanned?.quality || 0}%`
          );
        } else {
          setFingerprintMessage('No active pledges found.');
          setError('No active pledges found');
        }
      } else {
        setFingerprintMessage('No matching pledges found.');
        setError('No matching pledges found');
      }
    } catch (err) {
      console.error('Fingerprint scan error:', err);
      setFingerprintMessage(err.message || 'Failed to scan fingerprint');
      setError(err.message || 'Failed to scan fingerprint');
    } finally {
      setFingerprintScanning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const onShortcut = (item) => {
    if (item.key === 'scan') {
      handleFingerprintScan();
      return;
    }
    if (item.path) navigate(item.path);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            minHeight: 'calc(100vh - 64px)',
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#f4f6f8',
          }}
        >
          <CircularProgress size={36} />
        </Box>
      </>
    );
  }

  if (loadError && !userData) {
    return (
      <>
        <Navbar />
        <Container maxWidth="md" sx={{ mt: 3 }}>
          <Alert severity="error">{loadError}</Alert>
        </Container>
      </>
    );
  }

  const initials = (userData?.name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <>
      <Navbar />
      <Box
        sx={{
          minHeight: 'calc(100vh - 58px)',
          background:
            'radial-gradient(1200px 500px at 10% -10%, rgba(201,162,77,0.18), transparent 55%), radial-gradient(900px 420px at 95% 0%, rgba(29,78,216,0.12), transparent 50%), linear-gradient(180deg, #eef2f7 0%, #f7f8fb 40%, #f4f6f8 100%)',
          py: { xs: 2, md: 2.75 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }}>
          {/* Hero */}
          <Box
            sx={{
              mb: 2.25,
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              background: 'linear-gradient(120deg, #0b1220 0%, #16233a 48%, #1e3a5f 100%)',
              border: '1px solid rgba(212,175,106,0.28)',
              boxShadow: '0 16px 40px rgba(15,23,42,0.18)',
              color: '#f8fafc',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: -40,
                top: -40,
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(240,217,160,0.22), transparent 70%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'rgba(245,230,200,0.75)',
                  mb: 0.6,
                }}
              >
                GIRVI workspace
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 24, md: 30 },
                  fontWeight: 850,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  background: 'linear-gradient(90deg, #fff7e8, #f0d9a0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Dashboard
              </Typography>
              <Typography sx={{ mt: 0.75, fontSize: 13.5, color: 'rgba(226,232,240,0.78)', maxWidth: 460 }}>
                Create, check, and release pledges fast — or scan fingerprint to find active tickets.
              </Typography>
            </Box>
            <Chip
              label={userData?.companyName || 'GIRVI'}
              sx={{
                position: 'relative',
                zIndex: 1,
                height: 30,
                fontWeight: 700,
                bgcolor: 'rgba(240,217,160,0.14)',
                color: '#f5e6c8',
                border: '1px solid rgba(240,217,160,0.35)',
              }}
            />
          </Box>

          {/* Quick actions */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 1.75 },
              mb: 2,
              borderRadius: 3,
              border: '1px solid rgba(226,232,240,0.95)',
              bgcolor: 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#64748b',
                mb: 1.35,
                px: 0.5,
              }}
            >
              Quick actions
            </Typography>
            <Grid container spacing={1.35}>
              {ACTION_SHORTCUTS.map((item) => {
                const Icon = item.icon;
                const scanning = item.key === 'scan' && fingerprintScanning;
                return (
                  <Grid item xs={6} sm={3} key={item.key}>
                    <Button
                      fullWidth
                      onClick={() => onShortcut(item)}
                      disabled={scanning}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.85,
                        textAlign: 'left',
                        textTransform: 'none',
                        minHeight: 96,
                        px: 1.6,
                        py: 1.35,
                        borderRadius: 2.5,
                        border: item.primary ? `1px solid ${item.color}55` : '1px solid #e8edf3',
                        bgcolor: item.primary
                          ? `linear-gradient(160deg, ${item.bg}, #fff)`
                          : 'linear-gradient(180deg, #ffffff, #f8fafc)',
                        background: item.primary
                          ? `linear-gradient(155deg, ${item.bg}, #ffffff)`
                          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                        color: '#0f172a',
                        boxShadow: item.primary
                          ? `0 10px 22px ${item.color}22`
                          : '0 1px 2px rgba(15,23,42,0.04)',
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                        '&:hover': {
                          borderColor: `${item.color}77`,
                          boxShadow: `0 14px 26px ${item.color}24`,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: item.bg,
                          color: item.color,
                          boxShadow: `inset 0 0 0 1px ${item.color}22`,
                        }}
                      >
                        {scanning ? <CircularProgress size={16} sx={{ color: item.color }} /> : <Icon sx={{ fontSize: 22 }} />}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 780, lineHeight: 1.2 }}>
                          {scanning ? 'Scanning…' : item.label}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.3 }}>
                          {item.hint}
                        </Typography>
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {(fingerprintMessage && !showFingerprintResults) || error ? (
            <Alert
              severity={error ? 'error' : 'info'}
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => {
                setError(null);
                setFingerprintMessage('');
              }}
            >
              {error || fingerprintMessage}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            {/* Profile */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fff',
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: '#0f172a',
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    {initials || 'U'}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 750, color: '#0f172a' }} noWrap>
                      {userData?.name || 'User'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>Profile overview</Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.1}>
                  {[
                    { icon: <PersonIcon sx={{ fontSize: 16 }} />, label: 'Name', value: userData?.name },
                    { icon: <BusinessIcon sx={{ fontSize: 16 }} />, label: 'Company', value: userData?.companyName },
                    { icon: <BadgeIcon sx={{ fontSize: 16 }} />, label: 'License', value: userData?.licenseNumber },
                  ].map((row) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.25,
                        py: 1,
                        borderRadius: 1.75,
                        bgcolor: '#f8fafc',
                        border: '1px solid #eef2f7',
                      }}
                    >
                      <Box sx={{ color: '#64748b', display: 'grid', placeItems: 'center' }}>{row.icon}</Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>
                          {row.label}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }} noWrap>
                          {row.value || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <CallRemindersPanel />
            </Grid>

            {/* Transactions */}
            <Grid item xs={false} md={5} sx={{ display: { xs: 'none', md: 'block' } }} />
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fff',
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'rgba(29,78,216,0.08)',
                        color: '#1d4ed8',
                      }}
                    >
                      <TxIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 750, color: '#0f172a' }}>
                      Recent transactions
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    label={`${transactions.length}`}
                    sx={{ height: 22, fontWeight: 700, bgcolor: '#f1f5f9' }}
                  />
                </Stack>

                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', bgcolor: '#f8fafc', py: 1 }}>
                          Description
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', bgcolor: '#f8fafc', py: 1, width: 120 }}
                        >
                          Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.length > 0 ? (
                        transactions.slice(0, 12).map((transaction) => (
                          <TableRow key={transaction._id || `${transaction.description}-${transaction.date}`} hover>
                            <TableCell sx={{ fontSize: 13, py: 1.05, borderColor: '#f1f5f9' }}>
                              {transaction.description || 'No description'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 12.5, color: '#64748b', py: 1.05, borderColor: '#f1f5f9' }}>
                              {transaction.date ? formatDate(transaction.date) : '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ py: 4, color: '#94a3b8', border: 0 }}>
                            No transactions found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Fingerprint results */}
      <Dialog
        open={showFingerprintResults}
        onClose={() => setShowFingerprintResults(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 750 }}>Fingerprint scan results</Typography>
            <Chip label={`${fingerprintMatches.length} active`} color="primary" size="small" />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {fingerprintMatches.length > 0 ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {fingerprintMessage}
              </Alert>

              {(() => {
                const customerGroups = {};
                fingerprintMatches.forEach((pledge) => {
                  const customerCode = pledge.customer?.code;
                  if (!customerGroups[customerCode]) {
                    customerGroups[customerCode] = { customer: pledge.customer, pledges: [] };
                  }
                  customerGroups[customerCode].pledges.push(pledge);
                });

                return Object.values(customerGroups).map((group, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 750, mb: 1.25 }}>Ledger details</Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>Customer name</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 650 }}>
                            {group.customer.firstName} {group.customer.lastName || ''}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>Customer code</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{group.customer.code}</Typography>
                        </Grid>
                        {group.customer.mobileNo ? (
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>Mobile</Typography>
                            <Typography sx={{ fontSize: 14 }}>{group.customer.mobileNo}</Typography>
                          </Grid>
                        ) : null}
                        {group.customer.address ? (
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>Address</Typography>
                            <Typography sx={{ fontSize: 14 }}>{group.customer.address}</Typography>
                          </Grid>
                        ) : null}
                      </Grid>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography sx={{ fontSize: 14, fontWeight: 750, mb: 1 }}>
                        Active pledges ({group.pledges.length})
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ticket No</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Principal</TableCell>
                              <TableCell align="right">Rate</TableCell>
                              <TableCell align="right">Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.pledges.map((pledge) => (
                              <TableRow
                                key={pledge._id}
                                sx={{
                                  backgroundColor: pledge.fingerprintMatched
                                    ? (pledge.fingerprintMatchScore || 0) >= 90
                                      ? 'rgba(76, 175, 80, 0.1)'
                                      : (pledge.fingerprintMatchScore || 0) >= 70
                                        ? 'rgba(255, 193, 7, 0.1)'
                                        : 'rgba(33, 150, 243, 0.05)'
                                    : 'transparent',
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {pledge.fingerprintMatched ? <FingerprintIcon fontSize="small" color="primary" /> : null}
                                    <Typography variant="body2" fontWeight={pledge.fingerprintMatched ? 700 : 400}>
                                      {pledge.loanDetails.ticketNo}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{formatDate(pledge.date)}</TableCell>
                                <TableCell align="right">
                                  ₹{Number(pledge.loanDetails.principalAmount).toFixed(2)}
                                </TableCell>
                                <TableCell align="right">{pledge.loanDetails.rateOfInterest}%</TableCell>
                                <TableCell align="right">
                                  {pledge.fingerprintMatched ? (
                                    <Chip
                                      icon={<FingerprintIcon />}
                                      label={`Matched ${pledge.fingerprintMatchScore}%`}
                                      color={
                                        (pledge.fingerprintMatchScore || 0) >= 90
                                          ? 'success'
                                          : (pledge.fingerprintMatchScore || 0) >= 70
                                            ? 'warning'
                                            : 'primary'
                                      }
                                      size="small"
                                    />
                                  ) : (
                                    <Chip label="Other active" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography variant="body2" fontWeight={700}>
                                  Total
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={700}>
                                  ₹
                                  {group.pledges
                                    .reduce((sum, p) => sum + (p.loanDetails?.principalAmount || 0), 0)
                                    .toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell colSpan={2} />
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                ));
              })()}
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No matching pledges found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setShowFingerprintResults(false)}>Close</Button>
          <Button
            onClick={handleFingerprintScan}
            disabled={fingerprintScanning}
            startIcon={<FingerprintIcon />}
            variant="contained"
          >
            Scan again
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDashboard;
